import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Calendar, RefreshCw, Loader2, CheckCircle } from 'lucide-react';

const API_URL = window.location.origin;

const CoachSessionsTab = () => {
  const [clientSessions, setClientSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [completingSession, setCompletingSession] = useState(null);

  const fetchClientSessions = async () => {
    setLoadingSessions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/coach/client-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientSessions(response.data.clients || []);
    } catch (error) {
      console.error('Failed to fetch client sessions:', error);
      toast.error('Failed to load client sessions');
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleCompleteSession = async (clientId, sessionType) => {
    setCompletingSession(`${clientId}-${sessionType}`);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/coach/complete-session`,
        { client_id: clientId, session_type: sessionType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchClientSessions(); // Refresh data
    } catch (error) {
      console.error('Failed to complete session:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete session');
    } finally {
      setCompletingSession(null);
    }
  };

  useEffect(() => {
    fetchClientSessions();
  }, []);

  return (
    <div data-testid="coach-sessions-tab">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-heading text-xl font-semibold text-forest">Client Sessions</h2>
        <Button onClick={fetchClientSessions} variant="outline" size="sm" data-testid="refresh-sessions-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Track and record billable sessions with your clients. Click a session type to mark it complete - this will create a note for invoicing.
      </p>
      
      {/* Session Type Legend */}
      <div className="mb-4 p-3 bg-sand/30 rounded-lg">
        <p className="text-xs font-medium text-gray-600 mb-2">Session Types:</p>
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-forest"></span> Coaching Sessions (qty)</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Initial Alignment</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> Midpoint Alignment</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500"></span> Final Alignment</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-terracotta"></span> 360 Assessment</span>
        </div>
      </div>
      
      {loadingSessions ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-forest" />
        </div>
      ) : clientSessions.length === 0 ? (
        <Card className="card-base">
          <CardContent className="p-8 text-center">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-forest mb-2">No Clients Assigned</h3>
            <p className="text-gray-500">Client session information will appear here once you have assigned clients.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {clientSessions.map((client) => {
            const wallet = client.sessions_wallet || {};
            const hasAnySessions = (wallet.coaching_sessions > 0) || 
                                  (wallet.initial_alignment_meeting > 0) ||
                                  (wallet.midpoint_alignment_meeting > 0) ||
                                  (wallet.final_alignment_meeting > 0) ||
                                  (wallet.assessment_360 > 0);
            
            return (
              <Card key={client.id} className="card-base" data-testid={`client-session-card-${client.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-forest/10 flex items-center justify-center overflow-hidden">
                        {client.profile_image ? (
                          <img src={client.profile_image} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-forest font-semibold">
                            {client.full_name?.split(' ').map(n => n[0]).join('') || 'C'}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-forest">{client.full_name}</h3>
                        <p className="text-sm text-gray-500">{client.email}</p>
                      </div>
                    </div>
                    {/* Session Wallet Summary */}
                    {hasAnySessions && (
                      <div className="flex flex-wrap gap-1">
                        {wallet.coaching_sessions > 0 && (
                          <Badge className="bg-forest/10 text-forest text-xs">
                            {wallet.coaching_sessions} Sessions
                          </Badge>
                        )}
                        {wallet.initial_alignment_meeting > 0 && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">Initial</Badge>
                        )}
                        {wallet.midpoint_alignment_meeting > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Midpoint</Badge>
                        )}
                        {wallet.final_alignment_meeting > 0 && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">Final</Badge>
                        )}
                        {wallet.assessment_360 > 0 && (
                          <Badge className="bg-terracotta/10 text-terracotta text-xs">360</Badge>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {!hasAnySessions ? (
                    <div className="text-center py-4 bg-sand/20 rounded-lg">
                      <p className="text-sm text-gray-500">No sessions in wallet. Admin needs to assign sessions to this client.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Coaching Session - Quantity Based */}
                      <Button
                        variant="outline"
                        className={`justify-between h-auto py-3 px-4 ${wallet.coaching_sessions > 0 ? '' : 'opacity-50'}`}
                        disabled={wallet.coaching_sessions <= 0 || completingSession === `${client.id}-session`}
                        onClick={() => handleCompleteSession(client.id, 'session')}
                        data-testid={`complete-coaching-session-${client.id}`}
                      >
                        <div className="flex items-center">
                          {completingSession === `${client.id}-session` ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2 text-forest" />
                          )}
                          <span className="text-left">Coaching Session</span>
                        </div>
                        <Badge variant="secondary" className="ml-2">{wallet.coaching_sessions || 0}</Badge>
                      </Button>
                      
                      {/* Initial Alignment Meeting */}
                      <Button
                        variant="outline"
                        className={`justify-between h-auto py-3 px-4 ${wallet.initial_alignment_meeting > 0 ? '' : 'opacity-50'}`}
                        disabled={wallet.initial_alignment_meeting <= 0 || completingSession === `${client.id}-initial_alignment`}
                        onClick={() => handleCompleteSession(client.id, 'initial_alignment')}
                        data-testid={`complete-initial-alignment-${client.id}`}
                      >
                        <div className="flex items-center">
                          {completingSession === `${client.id}-initial_alignment` ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2 text-blue-500" />
                          )}
                          <span className="text-left">Initial Alignment</span>
                        </div>
                        <Badge variant="secondary" className="ml-2">{wallet.initial_alignment_meeting || 0}</Badge>
                      </Button>
                      
                      {/* Midpoint Alignment Meeting */}
                      <Button
                        variant="outline"
                        className={`justify-between h-auto py-3 px-4 ${wallet.midpoint_alignment_meeting > 0 ? '' : 'opacity-50'}`}
                        disabled={wallet.midpoint_alignment_meeting <= 0 || completingSession === `${client.id}-midpoint_alignment`}
                        onClick={() => handleCompleteSession(client.id, 'midpoint_alignment')}
                        data-testid={`complete-midpoint-alignment-${client.id}`}
                      >
                        <div className="flex items-center">
                          {completingSession === `${client.id}-midpoint_alignment` ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2 text-amber-500" />
                          )}
                          <span className="text-left">Midpoint Alignment</span>
                        </div>
                        <Badge variant="secondary" className="ml-2">{wallet.midpoint_alignment_meeting || 0}</Badge>
                      </Button>
                      
                      {/* Final Alignment Meeting */}
                      <Button
                        variant="outline"
                        className={`justify-between h-auto py-3 px-4 ${wallet.final_alignment_meeting > 0 ? '' : 'opacity-50'}`}
                        disabled={wallet.final_alignment_meeting <= 0 || completingSession === `${client.id}-final_alignment`}
                        onClick={() => handleCompleteSession(client.id, 'final_alignment')}
                        data-testid={`complete-final-alignment-${client.id}`}
                      >
                        <div className="flex items-center">
                          {completingSession === `${client.id}-final_alignment` ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2 text-purple-500" />
                          )}
                          <span className="text-left">Final Alignment</span>
                        </div>
                        <Badge variant="secondary" className="ml-2">{wallet.final_alignment_meeting || 0}</Badge>
                      </Button>
                      
                      {/* 360 Assessment & Feedback Session */}
                      <Button
                        variant="outline"
                        className={`justify-between h-auto py-3 px-4 ${wallet.assessment_360 > 0 ? '' : 'opacity-50'}`}
                        disabled={wallet.assessment_360 <= 0 || completingSession === `${client.id}-360_assessment`}
                        onClick={() => handleCompleteSession(client.id, '360_assessment')}
                        data-testid={`complete-360-assessment-${client.id}`}
                      >
                        <div className="flex items-center">
                          {completingSession === `${client.id}-360_assessment` ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2 text-terracotta" />
                          )}
                          <span className="text-left">360 Assessment</span>
                        </div>
                        <Badge variant="secondary" className="ml-2">{wallet.assessment_360 || 0}</Badge>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachSessionsTab;
